import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "components/ui/accordion";
import { Alert, AlertDescription } from "components/ui/alert";
import { Badge } from "components/ui/badge";
import { Checkbox } from "components/ui/checkbox";
import { Label } from "components/ui/label";
import { Separator } from "components/ui/separator";
import {
  BatchConfig,
  CheckedItems,
  OutputFormat,
  SetCheckedItems,
} from "pages/geoscape-batch/models";
import {
  OutputBatchConfigFormat,
  SetBatchConfig,
} from "pages/geoscape-batch/types";
import React, { useCallback, useEffect, useState } from "react";

import { TooltipWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { Gem } from "lucide-react";
import {
  premiumAdditionalParameters,
  premiumBatchPermissionKeys,
} from "pages/geoscape-batch/utils";
import {
  MANDATORY_ATTRIBUTES,
  PROPERTIES_DEFINITION,
  addIndex,
  transformString,
} from "./utils";

interface AccordionProps {
  outPutFormatDefinition: OutputBatchConfigFormat[];
  batchConfig: BatchConfig;
  title: string;
  setBatchConfig: SetBatchConfig;
  checkedItems: CheckedItems;
  setCheckedItems: SetCheckedItems;
  propertyCheckedItems: any;
  enrichmentRows: any;
  setEnrichmentRows: any;
}

export const AccordionComponent: React.FC<AccordionProps> = ({
  outPutFormatDefinition,
  batchConfig,
  title,
  setBatchConfig,
  checkedItems,
  setCheckedItems,
  enrichmentRows,
  setEnrichmentRows,
  propertyCheckedItems,
}) => {
  const [selectAll, setSelectAll] = React.useState(false);
  const [selectAllDisable, setselectAllDisable] = useState(false);

  const { isPermitted, permissionDeniedMessageString } = usePermissions();

  useEffect(() => {
    if (propertyCheckedItems) {
      const allChecked = outPutFormatDefinition.every(
        (option) => propertyCheckedItems[option.attributeName],
      );

      const allMandatory = outPutFormatDefinition.every((option) =>
        MANDATORY_ATTRIBUTES[title]?.includes(option.attributeName),
      );

      setSelectAll(allChecked);

      setCheckedItems((prev: CheckedItems) => ({
        ...prev,
        [title]: propertyCheckedItems,
      }));

      if (allChecked && allMandatory) setselectAllDisable(true);
    } else {
      setSelectAll(false);
    }
  }, [batchConfig.hubInfo.checkedItems[title]]);

  const handleCheckboxChange = useCallback(
    (
      attributeName: string,
      accordionKey: string,
      index: number,
      checked: boolean,
    ) => {
      setCheckedItems((prev: CheckedItems) => ({
        ...prev,
        [title]: { ...prev[title], [`${attributeName}`]: checked },
      }));

      const option = outPutFormatDefinition[index];

      setEnrichmentRows((prev: OutputFormat[]) => {
        return checked
          ? addIndex([...prev, option])
          : addIndex(
              prev.filter(
                (item: OutputFormat) =>
                  item.attributeName !== option.attributeName ||
                  item.source !== option.source,
              ),
            );
      });
    },
    [outPutFormatDefinition, setBatchConfig, title],
  );

  useEffect(() => {
    setSelectAll(
      checkedItems[title] && Object.values(checkedItems[title]).every(Boolean),
    );
  }, [checkedItems[title]]);

  const handleSelectAllChange = useCallback(
    (checked: boolean) => {
      setSelectAll(checked);

      const newCheckedItems: { [key: string]: boolean } = {};

      if (outPutFormatDefinition) {
        const mandatoryAttributes = outPutFormatDefinition.filter((option) =>
          MANDATORY_ATTRIBUTES[title]?.includes(option.attributeName),
        );

        outPutFormatDefinition.forEach((option) => {
          newCheckedItems[option.attributeName] = checked;
        });

        mandatoryAttributes.forEach((option) => {
          newCheckedItems[option.attributeName] = true;
        });
      }

      setCheckedItems((prev: CheckedItems) => ({
        ...prev,
        [title]: newCheckedItems,
      }));

      const attributesPropertyOutput =
        outPutFormatDefinition.filter(
          (option) =>
            !MANDATORY_ATTRIBUTES[title]?.includes(option.attributeName),
        ) || [];

      setEnrichmentRows((prev: OutputFormat[]) => {
        const currentOutputFormat: OutputFormat[] = prev.filter(
          (item: OutputFormat) =>
            attributesPropertyOutput.some(
              (option) =>
                option.attributeName === item.attributeName &&
                item.property == option.property,
            ),
        );

        return checked
          ? addIndex([
              ...prev,
              ...attributesPropertyOutput.filter(
                (option) =>
                  !currentOutputFormat.some(
                    (item: OutputFormat) =>
                      item.attributeName === option.attributeName &&
                      item.property == option.property,
                  ),
              ),
            ])
          : addIndex(
              prev.filter(
                (item: OutputFormat) =>
                  !attributesPropertyOutput.some(
                    (option) =>
                      option.attributeName === item.attributeName &&
                      item.property == option.property,
                  ),
              ),
            );
      });
    },
    [outPutFormatDefinition, setBatchConfig, title],
  );

  const isPremiumDenied =
    !isPermitted(premiumBatchPermissionKeys) &&
    premiumAdditionalParameters.includes(title);

  return (
    <AccordionItem value={title}>
      <AccordionTrigger>
        <div className="flex w-full justify-between items-center">
          <div className="flex flex-row gap-2 items-center">
            <span>{transformString(title)}</span>
            {premiumAdditionalParameters.includes(title) && (
              <Badge variant="outline" className="gap-1">
                <Gem className="size-3 text-info" />
                Premium
              </Badge>
            )}
          </div>
          {checkedItems[title] &&
            Object.values(checkedItems[title]).filter(Boolean).length !== 0 && (
              <Badge variant="outline" className="mr-2">
                {Object.values(checkedItems[title]).filter(Boolean).length}{" "}
                attributes selected
              </Badge>
            )}
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <p className="text-sm">{PROPERTIES_DEFINITION[title]}</p>
        {title == "seifa" && <Overview />}

        <div className="ml-2.5 mb-2.5 mt-2">
          <TooltipWrapper
            title={permissionDeniedMessageString(premiumBatchPermissionKeys)}
          >
            <div className="flex items-center gap-2">
              <Checkbox
                id={`select-all-${title}`}
                disabled={selectAllDisable || isPremiumDenied}
                checked={selectAll}
                onCheckedChange={(checked) =>
                  handleSelectAllChange(checked === true)
                }
              />
              <Label
                htmlFor={`select-all-${title}`}
                className="font-normal cursor-pointer"
              >
                Select All
              </Label>
            </div>
          </TooltipWrapper>
        </div>

        {title == "solarPanelBundle" && (
          <Alert variant="info" className="mb-2.5">
            <AlertDescription>
              3 Additional Credits Per Address Feature
            </AlertDescription>
          </Alert>
        )}

        <Separator />

        <div className="grid grid-cols-1 sm:grid-cols-2 w-full mt-2">
          {outPutFormatDefinition.map((option, index) => {
            const isMandatory = MANDATORY_ATTRIBUTES[title]?.includes(
              option.attributeName,
            );
            const id = `${title}-${option.attributeName}`;
            return (
              <div key={index} className="py-1 min-w-0">
                <TooltipWrapper
                  title={permissionDeniedMessageString(
                    premiumBatchPermissionKeys,
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={id}
                      checked={
                        !!checkedItems[title]?.[option.attributeName] || false
                      }
                      disabled={isMandatory || isPremiumDenied}
                      onCheckedChange={(checked) =>
                        handleCheckboxChange(
                          option.attributeName,
                          title,
                          index,
                          checked === true,
                        )
                      }
                    />
                    <Label
                      htmlFor={id}
                      className="font-normal cursor-pointer"
                    >
                      {transformString(option.attributeName)}
                    </Label>
                  </div>
                </TooltipWrapper>
              </div>
            );
          })}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

const Overview = () => {
  return (
    <ul className="space-y-2 mt-2">
      <li>
        <p className="text-sm font-medium">
          Index of Relative Socio-economic Advantage and Disadvantage (IRSAD)
        </p>
        <p className="text-sm text-muted-foreground">
          Measures community well-being through advantage and disadvantage
          scores. It ranks areas, showing where they stand nationally and by
          state, in terms of overall socio-economic conditions
        </p>
      </li>
      <li>
        <p className="text-sm font-medium">Index of Economic Resources (IER)</p>
        <p className="text-sm text-muted-foreground">
          Assesses areas based on economic resources, highlighting differences
          in wealth. It includes scores and rankings to identify economic
          disparities at national and state levels
        </p>
      </li>
      <li>
        <p className="text-sm font-medium">
          Index of Education and Occupation (IEO)
        </p>
        <p className="text-sm text-muted-foreground">
          Evaluates education levels and occupational status, offering insights
          into educational and job opportunities. Rankings help compare areas'
          educational and work-related achievements
        </p>
      </li>
      <li>
        <p className="text-sm font-medium">
          General Understanding (For Deciles and Percentiles)
        </p>
        <p className="text-sm text-muted-foreground">
          Each theme uses deciles and percentiles to simplify comparisons.
          Deciles divide areas into ten parts based on their scores—higher
          deciles mean better standing. Percentiles offer a more precise
          position, with higher values indicating top ranks
        </p>
      </li>
    </ul>
  );
};
